/**
 * Alertes : erreur, avertissement, succès.
 *
 * Les alertes reçoivent un `MessageDescriptor` et non une chaîne : la mise en forme a lieu
 * ici, au moment de l'affichage. C'est ce qui fait qu'une erreur survenue avant un
 * changement de langue s'affiche dans la nouvelle langue, et non dans celle où elle a été
 * produite.
 *
 * Chaque message d'erreur doit dire **quoi faire**, pas seulement ce qui a échoué.
 */

import { formatMessage, useT, type MessageDescriptor } from '../i18n/index.js';

interface ErrorAlertProps {
  message: MessageDescriptor;
  /** Titre de l'alerte. Par défaut, un libellé générique de transfert interrompu. */
  title?: string;
  onRetry?: () => void;
  retryLabel?: string;
}

export function ErrorAlert({
  message,
  title,
  onRetry,
  retryLabel,
}: ErrorAlertProps): React.JSX.Element {
  const translator = useT();
  const heading = title ?? translator.t('room.errorTitle');

  return (
    <div className="alert" data-tone="danger" role="alert">
      <p className="alert-title">{heading}</p>
      <p>{formatMessage(translator, message)}</p>
      {onRetry && (
        <p style={{ marginTop: '0.85rem' }}>
          <button type="button" className="btn btn-sm" onClick={onRetry}>
            {retryLabel ?? translator.t('alert.retry')}
          </button>
        </p>
      )}
    </div>
  );
}

export function WarningAlert({ message }: { message: MessageDescriptor }): React.JSX.Element {
  const translator = useT();
  return (
    <div className="alert" data-tone="warning" role="status">
      <p>{formatMessage(translator, message)}</p>
    </div>
  );
}

export function SuccessAlert({
  title,
  children,
}: {
  title: string;
  children?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="alert" data-tone="success" role="status">
      <p className="alert-title">{title}</p>
      {children}
    </div>
  );
}
